import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@mui/material';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { TruckShiftEntity } from 'src/entities/TruckShift.entity';
import { GridSelection } from 'src/pages/TruckShifts/bulkSelection';
import { CompanyGroupPresenter } from 'src/pages/TruckShifts/presenters/CompanyGroup.presenter';
import { HeaderCellsPresenter } from 'src/pages/TruckShifts/presenters/HeaderCells.presenter';
import { groupByPartnerCompany, ShiftCell } from 'src/pages/TruckShifts/truckShiftGrid';

type Props = {
  trucks: TruckShiftEntity[];
  dateColumns: string[];
  selection?: GridSelection;
  // CompanyGroupPresenter/BodyRowPresenter には selection オブジェクトではなく、実体の Set をそのまま渡す。
  // Set 自体は選択操作で変化しない限り同一参照が保たれるため、memo 化した配下コンポーネントの
  // 不要な全再レンダーを避けられる（selection オブジェクトは selectedTruckIds/selectedDates の
  // どちらかが変わるたびに新しい参照になってしまうため、そのままでは渡せない）。
  selectedTruckIds?: Set<number>;
  selectedDates?: Set<string>;
  onCellClick?: (truckId: number, date: string, cells: ShiftCell[], anchorEl: HTMLElement) => void;
};

export const TablePresenter: FC<Props> = ({ trucks, dateColumns, selection, selectedTruckIds, selectedDates, onCellClick }) => {
  const theme = useTheme();
  const groups = useMemo(() => groupByPartnerCompany(trucks), [trucks]);
  const [collapsed, setCollapsed] = useState<Set<number | null>>(new Set());

  const toggle = useCallback((companyId: number | null) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId); else next.add(companyId);
      return next;
    });
  }, []);

  // CompanyGroupPresenter(memo) に渡す onToggle は companyId ごとに参照を固定する。
  // toggle は useCallback で安定しているが、`() => toggle(group.companyId)` を groups.map 内で
  // 都度生成すると呼ぶたびに新しい関数になり、対象外のグループまで memo bail-out に失敗して
  // 全行が再レンダーされてしまう（C3 の目的=トグルした行だけ再レンダーが未達になる）。
  const toggleHandlersRef = useRef(new Map<number | null, () => void>());
  const getToggleHandler = (companyId: number | null): (() => void) => {
    const cache = toggleHandlersRef.current;
    let handler = cache.get(companyId);
    if (!handler) {
      handler = () => toggle(companyId);
      cache.set(companyId, handler);
    }
    return handler;
  };

  // 削除済み（active=false）トラックは一括選択の対象外（CompanyGroupPresenter と同じ不変条件）。
  const allIds = trucks.filter((t) => t.active !== false).map((t) => t.id);
  const selectedCount = selection ? allIds.filter((id) => selection.isTruckSelected(id)).length : 0;
  const allSelected = selectedCount > 0 && selectedCount === allIds.length;

  return (
    <TableContainer component={Paper} sx={{ maxHeight: `calc(100vh - ${theme.spacing(20)})`, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ width: 'auto' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 280, minWidth: 280, py: 0.75, px: 1.5, fontSize: 12, fontWeight: 700, position: 'sticky', left: 0, zIndex: 3, backgroundColor: 'background.paper', borderRight: '1px solid', borderColor: 'divider' }}>
              <Box display="flex" alignItems="center" gap={1}>
                車両（{trucks.length}台）
                {selection && (
                  <Button
                    size="small"
                    onClick={() => selection.setTrucksSelected(allIds, !allSelected)}
                    sx={{ textTransform: 'none', minWidth: 0, px: 1, py: 0, fontSize: 12 }}
                  >
                    {allSelected ? '全解除' : '全選択'}
                  </Button>
                )}
              </Box>
            </TableCell>
            <HeaderCellsPresenter dateColumns={dateColumns} selection={selection} />
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <CompanyGroupPresenter
              key={String(group.companyId)}
              group={group}
              dateColumns={dateColumns}
              expanded={!collapsed.has(group.companyId)}
              onToggle={getToggleHandler(group.companyId)}
              showCheckbox={selection != null}
              selectedTruckIds={selectedTruckIds}
              selectedDates={selectedDates}
              onToggleTruck={selection?.toggleTruck}
              onSetTrucksSelected={selection?.setTrucksSelected}
              onCellClick={onCellClick}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
